import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { IoSend } from "react-icons/io5";
import { getScenarios } from "../../API";
import { sendTicketComment } from "../../apis";
import SelectDropdown from "../../ui/forms/SelectDropdown";
import { showToast } from "../../utils/handleToast";

const EmailReplyModal = ({ show, setShow, selectedEmail, borrowerEmail, emailData = [] }) => {
  const [message, setMessage] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(""); // This state holds the scenario ID
  const [selectedEmailAddress, setSelectedEmailAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (show) {
      // Check if any emails have valid ticket IDs
      const validTicketEmails = emailData.filter(email => email.ticket && email.ticket !== "null");
      
      if (validTicketEmails.length === 0) {
        showToast({ 
          variant: "warning", 
          message: "Cannot reply as the customer hasn't replied yet. No valid ticket IDs found." 
        });
        setShow(false);
        return;
      }
      
      fetchScenarios();
      // Set email address from borrower details (priority) or selected email
      if (borrowerEmail) {
        setSelectedEmailAddress(borrowerEmail);
      } else if (selectedEmail?.from_mail) {
        setSelectedEmailAddress(selectedEmail.from_mail);
      }
    }
  }, [show, selectedEmail, borrowerEmail, emailData, setShow]);

  const fetchScenarios = async () => {
    setIsLoading(true);
    try {
      const response = await getScenarios();
      if (response && response.status && response.data) {
        setScenarios(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch scenarios:", error);
      showToast({ variant: "danger", message: "Failed to fetch scenarios" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectScenario = (value) => {
    if (!value) return;
    
    const selectedScenarioObj = scenarios.find(scenario => scenario.id === value);
    if (selectedScenarioObj) {
      setMessage(selectedScenarioObj.text);
      setSelectedScenario(value); // 'value' is the scenario_id
    }
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      showToast({ variant: "warning", message: "Please enter a message" });
      return;
    }

    if (!selectedEmailAddress.trim()) {
      showToast({ variant: "warning", message: "Please enter an email address" });
      return;
    }

    // Find an email with a valid ticket ID
    const validTicketEmails = emailData.filter(email => email.ticket && email.ticket !== "null");
    
    if (validTicketEmails.length === 0) {
      showToast({ 
        variant: "warning", 
        message: "Cannot reply as the customer hasn't replied yet. No valid ticket IDs found." 
      });
      return;
    }
    
    // Use the first available valid ticket ID
    const ticketId = validTicketEmails[0].ticket;

    setIsSending(true);
    
    try {
      const requestBody = {
        ticket_id: parseInt(ticketId),
        ticket_comment: message
      };

      if (selectedScenario) {
        requestBody.scenario_id = selectedScenario;
      }

      console.log("Sending ticket comment:", requestBody);
      
      const response = await sendTicketComment(requestBody);
      
      if (response && response.data && response.data.status === true) {
        showToast({ variant: "success", message: response.data.message || "Email reply sent successfully" });
        handleClose();
      } else {
        throw new Error(response?.data?.message || "Failed to send reply");
      }
    } catch (error) {
      console.error("Failed to send ticket comment:", error);
      showToast({ 
        variant: "danger", 
        message: error.response?.data?.message || "Failed to send email reply. Please try again." 
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setMessage("");
    setSelectedScenario("");
    setSelectedEmailAddress("");
  };

  // Transform scenarios data to match SelectDropdown format
  const scenarioOptions = scenarios.map(scenario => ({
    value: scenario.id,
    label: scenario.scenario_name
  }));

  return (
    <Modal show={show} onHide={handleClose} size="lg" style={{"z-index": "9999"}}>
      <Modal.Header closeButton>
        <Modal.Title>Send Email Reply</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedEmail && (
          <div className="mb-3">
            <p><strong>Replying to:</strong> {selectedEmail.subject}</p>
          </div>
        )}
        
        <div className="mb-3">
          <label className="form-label">Email Address (To)</label>
          <div className="form-control-plaintext bg-light p-2 rounded">
            <strong>{selectedEmailAddress || "No email address available"}</strong>
          </div>
          {selectedEmailAddress && (
            <small className="text-muted">Auto-selected from borrower details</small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Select Scenario</label>
          <SelectDropdown
            placeholder="Select Scenario"
            options={scenarioOptions}
            value={selectedScenario}
            onChange={handleSelectScenario}
            isLoading={isLoading}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Message</label>
          <textarea
            className="form-control"
            rows="8"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your email reply here..."
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!message.trim() || isSending}>
          <IoSend className="me-2" />
          {isSending ? "Sending..." : "Send Reply"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailReplyModal;